import { useListenStyles } from "./listenStil";
import { istObjekt } from "./listenStil";

/**
 * Versicherungsbedarf je Sparte — Fachabstimmung 31.08.2026: *„die fehlen
 * mir: Mietverlust, Glas"*. Bis 10/2026 gab es im Immobilien-Formular gar keine
 * Bedarfs- oder Spartenauswahl.
 *
 * Gespeichert unter EINEM Schlüssel `versicherungsbedarf`:
 *   `{ glas: "bedarf", mietverlust: "besteht", gebaeude: "kein", … }`
 *
 * Einige Sparten erscheinen nur, wenn sie zum Gebäude passen (Mietverlust nur
 * bei Vermietung, Gewässerschaden nur mit Öltank, Photovoltaik nur mit PV) —
 * eine schon getroffene Auswahl bleibt aber immer sichtbar.
 */

export type BedarfStatus = "bedarf" | "besteht" | "kein";

export type BedarfSparte = {
  key: string;
  label: string;
  hinweis?: string;
  /** Nur anzeigen, wenn das Gebäude dazu passt. */
  wenn?: (a: Record<string, unknown>) => boolean;
};

const heizung = (a: Record<string, unknown>, name: string): Record<string, unknown> | undefined => {
  const h = a.heizungen;
  return istObjekt(h) && istObjekt(h[name]) ? (h[name] as Record<string, unknown>) : undefined;
};

export const istVermietet = (a: Record<string, unknown>): boolean =>
  a.vermietung === "ja" || a.eigennutzung === "nein" || a.eigennutzung === "teilweise";

export const hatOeltank = (a: Record<string, unknown>): boolean => {
  const u = a.umwelt;
  return istObjekt(u) && istObjekt(u["Öltanks"]) && (u["Öltanks"] as Record<string, unknown>).selected === "true";
};

export const hatPhotovoltaik = (a: Record<string, unknown>): boolean => {
  const pv = heizung(a, "Solar-/Photovoltaikanlage");
  return !!pv && pv.selected === "true" && pv.art === "Photovoltaik";
};

export const BEDARF_SPARTEN: readonly BedarfSparte[] = [
  { key: "gebaeude", label: "Gebäudeversicherung", hinweis: "Feuer, Leitungswasser, Sturm/Hagel" },
  { key: "elementar", label: "Elementarschäden", hinweis: "Überschwemmung, Starkregen, Rückstau" },
  { key: "glas", label: "Glasversicherung" },
  {
    key: "mietverlust",
    label: "Mietverlust / Mietausfall",
    hinweis: "Mietausfall nach einem versicherten Schaden",
    wenn: istVermietet,
  },
  { key: "hug", label: "Haus- und Grundbesitzerhaftpflicht" },
  { key: "gewaesser", label: "Gewässerschadenhaftpflicht", hinweis: "für den Öltank", wenn: hatOeltank },
  { key: "photovoltaik", label: "Photovoltaikversicherung", wenn: hatPhotovoltaik },
  {
    key: "bauleistung",
    label: "Bauleistung / Bauherrenhaftpflicht",
    hinweis: "für die geplante Sanierung",
    wenn: (a) => a.sanierungGeplant === "ja",
  },
];

const STATUS: readonly { value: BedarfStatus; label: string }[] = [
  { value: "bedarf", label: "Bedarf" },
  { value: "besteht", label: "besteht bereits" },
  { value: "kein", label: "kein Bedarf" },
];

export const asBedarf = (v: unknown): Record<string, BedarfStatus> => {
  if (!istObjekt(v)) return {};
  const out: Record<string, BedarfStatus> = {};
  for (const [k, w] of Object.entries(v)) {
    if (w === "bedarf" || w === "besteht" || w === "kein") out[k] = w;
  }
  return out;
};

/** Die Sparten, die für dieses Gebäude angeboten werden. */
export const sichtbareSparten = (a: Record<string, unknown>): BedarfSparte[] => {
  const gewaehlt = asBedarf(a.versicherungsbedarf);
  return BEDARF_SPARTEN.filter((s) => !s.wenn || s.wenn(a) || s.key in gewaehlt);
};

type Props = {
  answers: Record<string, unknown>;
  onChange: (next: Record<string, BedarfStatus>) => void;
};

const VersicherungsbedarfBlock = ({ answers, onChange }: Props) => {
  const styles = useListenStyles();
  const werte = asBedarf(answers.versicherungsbedarf);
  return (
    <div className={styles.root}>
      {sichtbareSparten(answers).map((sparte) => (
        <div key={sparte.key} className={styles.card} role="group" aria-label={sparte.label}>
          <div className={styles.head}>
            <span className={styles.title}>{sparte.label}</span>
          </div>
          {sparte.hinweis && <span className={styles.intro}>{sparte.hinweis}</span>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {STATUS.map((s) => (
              <label key={s.value} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="radio"
                  name={`versicherungsbedarf-${sparte.key}`}
                  checked={werte[sparte.key] === s.value}
                  onChange={() => onChange({ ...werte, [sparte.key]: s.value })}
                />
                <span className={styles.intro}>{s.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VersicherungsbedarfBlock;
